import React, { useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode"; // ✅ Correct import
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../../config";

const baseUrl = config.baseURL;

const AddNewUser = () => {
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("user");
  const [password, setPassword] = useState("");
  const [companyId, setCompanyId] = useState(null);

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  // Decode token only once
  useEffect(() => {
    if (token) {
      const decoded = jwtDecode(token);
      setCompanyId(decoded.companyId);
    }
  }, [token]);

  // Default access modules
  const [access, setAccess] = useState({
    customer: { add: false, edit: false, show: false, delete: false },
    categories: { add: false, edit: false, show: false, delete: false },
    complaint: { add: false, show: false },
    earnings: { add: false, edit: false, show: false, delete: false },
    expenses: { add: false, edit: false, show: false, delete: false },
    faqs: { show: false },
    invoice: { show: false },
    materials: { add: false, edit: false, show: false, delete: false },
    orders: { add: false, edit: false, show: false, delete: false },
    pages: { add: false, edit: false, show: false, delete: false },
    ProductionBatch: { add: false, edit: false, show: false, delete: false },
    products: { add: false, edit: false, show: false, delete: false },
    rewardPoints: { add: false, edit: false, show: false, delete: false },
    ShippingCompany: { add: false, edit: false, show: false, delete: false },
    suppliers: { add: false, edit: false, show: false, delete: false },
    rewardsettings: { add: false, edit: false, show: false, delete: false },
    pricing: {},
    careers: {},
    zones: {},
    marketing: { add: false, edit: false, show: false, delete: false },
    ads: { add: false, edit: false, show: false, delete: false },
    advertiser: { add: false, edit: false, show: false, delete: false },
  });

  // Role change
  const handleRoleChange = (e) => setRole(e.target.value);

  // Checkbox changes
  const handleAccessChange = (module, action, e) => {
    setAccess((prev) => ({
      ...prev,
      [module]: {
        ...prev[module],
        [action]: e.target.checked,
      },
    }));
  };

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!companyId) {
      toast.error("Company ID is required. Please log in again.");
      return;
    }

    if (!firstname || !lastname || !email || !password) {
      toast.error("All fields are required");
      return;
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      toast.error("Invalid email format");
      return;
    }

    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    try {
      const payload = {
        firstname,
        lastname,
        email,
        role,
        access,
        password,
        companyId,
      };

      const response = await fetch(`${baseUrl}/api/newUser`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      // 🔥 Always parse JSON (even on 400)
      const data = await response.json();

      console.log("Backend response:", data);

      // ❌ HTTP error (400 / 401 / 500)
      if (!response.ok) {
        const messageId = Number(data?.messageId);
        const errorMessage = data?.message || "Failed to add user";

        switch (messageId) {
          case 1:
            toast.error(
              "No pricing plan found for this company. Please assign one."
            );
            break;
          case 2:
            toast.error("User limit reached for this subscription plan.");
            break;
          case 3:
            toast.error("Storage limit exceeded. Please upgrade plan.");
            break;
          case 0:
            toast.success(
              "User successfully created , please verify it with Email"
            );
            break;
          default:
            toast.error(errorMessage);
        }

        return;
      }

      // ✅ SUCCESS
      toast.success(data?.message || "User added successfully");
      navigate("/dashboard/UsersPage");
    } catch (error) {
      console.error("❌ Fetch error:", error);

      toast.error(
        error?.message ||
          "Network or server error. Please check your connection."
      );
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "#F4F6F9",
        minHeight: "100vh",
        padding: "20px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Typography
        variant="h4"
        sx={{
          fontFamily: "Playfair Display",
          fontWeight: "bold",
          marginBottom: "24px",
          color: "#333333",
          width: "100%",
          maxWidth: "700px",
          textAlign: "left",
        }}
      >
        Add New User
      </Typography>
      <ToastContainer />
      <Paper
        elevation={3}
        sx={{
          padding: "30px",
          width: "100%",
          maxWidth: "700px",
          borderRadius: "10px",
          backgroundColor: "#FFFFFF",
        }}
      >
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First Name"
                variant="outlined"
                fullWidth
                value={firstname}
                onChange={(e) => setFirstname(e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last Name"
                variant="outlined"
                fullWidth
                value={lastname}
                onChange={(e) => setLastname(e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                variant="outlined"
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Password"
                variant="outlined"
                type="password"
                fullWidth
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </Grid>

            {/* <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Role</InputLabel>
                <Select label="Role" value={role} onChange={handleRoleChange}>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="user">User</MenuItem>
                </Select>
              </FormControl>
            </Grid> */}

            {role === "admin" ? (
              <Grid item xs={12}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    marginTop: "16px",
                    color: "#2E4053",
                    fontWeight: "bold",
                  }}
                >
                  Admin has access to all modules. No need to assign specific
                  permissions.
                </Typography>
              </Grid>
            ) : (
              <Grid item xs={12}>
                <Typography
                  variant="h6"
                  sx={{
                    marginTop: "16px",
                    marginBottom: "16px",
                    color: "#333333",
                  }}
                >
                  Assign Access to Modules
                </Typography>
                <Grid container spacing={2}>
                  {Object.keys(access).map((module) => (
                    <Grid item xs={12} sm={6} md={4} key={module}>
                      <Accordion
                        sx={{
                          "& .MuiAccordionSummary-root": {
                            backgroundColor: "#F4F6F9",
                          },
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMore />}
                          aria-controls={`${module}-content`}
                          id={`${module}-header`}
                        >
                          <Typography sx={{ fontWeight: "bold" }}>
                            {module.charAt(0).toUpperCase() + module.slice(1)}{" "}
                            Module
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <FormGroup>
                            {module === "pricing" ||
                            module === "careers" ||
                            module === "zones" ? (
                              <Typography variant="body2" color="textSecondary">
                                No actions available.
                              </Typography>
                            ) : (
                              ["add", "edit", "show", "delete"].map(
                                (action) => {
                                  if (
                                    (module === "faqs" ||
                                      module === "invoice") &&
                                    action !== "show"
                                  )
                                    return null;
                                  if (
                                    access[module] &&
                                    access[module].hasOwnProperty(action)
                                  ) {
                                    return (
                                      <FormControlLabel
                                        key={action}
                                        control={
                                          <Checkbox
                                            checked={access[module][action]}
                                            onChange={(e) =>
                                              handleAccessChange(
                                                module,
                                                action,
                                                e
                                              )
                                            }
                                          />
                                        }
                                        label={`Can ${action} ${
                                          module.charAt(0).toUpperCase() +
                                          module.slice(1)
                                        }`}
                                      />
                                    );
                                  }
                                  return null;
                                }
                              )
                            )}
                          </FormGroup>
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            )}

            <Grid item xs={12}>
              <Grid container spacing={2}>
                {/* Cancel Button */}

                {/* Submit Button */}
                <Grid item xs={12} sm={6}>
                  <Button
                    type="submit"
                    variant="contained"
                    sx={{
                      backgroundColor: "#2E4053",
                      color: "#FFFFFF",
                      padding: "12px 24px",
                      fontSize: "16px",
                      fontWeight: "bold",
                      borderRadius: "8px",
                      width: "100%",
                      marginTop: "20px",
                      "&:hover": { backgroundColor: "#34495E" },
                    }}
                  >
                    Add User
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={() => navigate("/dashboard/UsersPage")}
                    sx={{
                      marginTop: "10px",
                      color: "#FFFFFF",
                      padding: "12px 24px",
                      fontSize: "16px",
                      fontWeight: "bold",
                      borderRadius: "8px",
                      width: "100%",
                      marginTop: "20px",
                      backgroundColor: "darkred",
                      "&:hover": {
                        backgroundColor: "#660000", // Darker red for hover state
                      },
                    }}
                  >
                    Cancel
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default AddNewUser;
